if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.form.querySelector("[name=id]").disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.addEventListener("pointerdown", this.onDynamicCheckoutStart.bind(this), {
          passive: true,
        });
        this.addEventListener("click", this.onDynamicCheckoutStart.bind(this));
      }

      onDynamicCheckoutStart(event) {
        const checkoutButton = event.target.closest?.(
          ".shopify-payment-button__button--unbranded, .shopify-payment-button [role='button']"
        );
        if (!checkoutButton) return;

        this.classList.add("silsila-checkout-pending");
        checkoutButton.setAttribute("aria-busy", "true");

        clearTimeout(this.checkoutFeedbackTimer);
        this.checkoutFeedbackTimer = setTimeout(() => {
          this.classList.remove("silsila-checkout-pending");
          checkoutButton.removeAttribute("aria-busy");
        }, 10000);
      }

      applyOptimisticCartCount(delta) {
        const cartIcon = document.getElementById("cart-icon-bubble");
        if (!cartIcon || !Number.isFinite(delta) || delta < 1) return;

        this.optimisticCartIconHTML = cartIcon.innerHTML;
        let bubble = cartIcon.querySelector(".cart-count-bubble");
        const currentCount = Number(
          bubble?.querySelector('[aria-hidden="true"]')?.textContent || 0
        );
        const nextCount = currentCount + delta;

        if (!bubble) {
          bubble = document.createElement("div");
          bubble.className = "cart-count-bubble";
          bubble.innerHTML =
            '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
          cartIcon.appendChild(bubble);
        }

        bubble.querySelector('[aria-hidden="true"]').textContent = nextCount;
        bubble.querySelector(".visually-hidden").textContent =
          `${nextCount} ${nextCount === 1 ? "item" : "items"}`;
      }

      restoreOptimisticCartCount() {
        const cartIcon = document.getElementById("cart-icon-bubble");
        if (cartIcon && typeof this.optimisticCartIconHTML === "string") {
          cartIcon.innerHTML = this.optimisticCartIconHTML;
        }
        this.optimisticCartIconHTML = null;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.submitButton
          .querySelector(".loading-overlay-spinner")
          ?.classList.remove("hidden");

        const optimisticQuantity = Number(
          this.form.querySelector('[name="quantity"]')?.value || 1
        );
        this.applyOptimisticCartCount(optimisticQuantity);

        const quickAddModal = this.closest("quick-add-modal");
        this.optimisticCartOpened = false;
        if (
          this.cart?.tagName === "CART-DRAWER" &&
          !quickAddModal &&
          typeof this.cart.open === "function"
        ) {
          this.optimisticCartOpened = !this.cart.classList.contains("active");
          this.cart.classList.add("is-updating");
          this.cart.open();
        }

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.restoreOptimisticCartCount();
              this.cart?.classList.remove("is-updating");
              if (this.optimisticCartOpened && typeof this.cart?.close === "function") {
                this.cart.close();
              }
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, { source: "product-form" });
            this.error = false;
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            this.restoreOptimisticCartCount();
            this.cart?.classList.remove("is-updating");
            if (this.optimisticCartOpened && typeof this.cart?.close === "function") {
              this.cart.close();
            }
            console.error(e);
          })
          .finally(() => {
            this.optimisticCartIconHTML = null;
            this.cart?.classList.remove("is-updating");
            this.submitButton.classList.remove("loading");
            this.submitButton
              .querySelector(".loading-overlay-spinner")
              ?.classList.add("hidden");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          if (typeof errorMessage === "object") {
            const formattedMessage = Object.entries(errorMessage)
              .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
              .join(" | ");
            this.errorMessage.textContent = formattedMessage;
          } else {
            this.errorMessage.textContent = errorMessage;
          }
        }
      }
    }
  );
  
  let removedElements = {
    mobile: [],
    desktop: [],
  };

  function removeElementsByScreenSize(container = document) {
    if (window.matchMedia("(max-width: 990px)").matches) {
      document.querySelectorAll(".hide-mobile").forEach((el) => {
        removedElements.desktop.push({
          parent: el.parentNode,
          element: el,
          nextSibling: el.nextSibling,
        });
        el.remove();
      });

      removedElements.mobile.forEach(({ parent, element, nextSibling }) => {
        if (nextSibling && nextSibling.parentNode === parent) {
          parent.insertBefore(element, nextSibling);
        } else {
          parent.appendChild(element);
        }
      });
      removedElements.mobile = [];
    } else {
      document.querySelectorAll(".hide-desktop").forEach((el) => {
        removedElements.mobile.push({
          parent: el.parentNode,
          element: el,
          nextSibling: el.nextSibling,
        });
        el.remove();
      });
      removedElements.desktop.forEach(({ parent, element, nextSibling }) => {
        if (nextSibling && nextSibling.parentNode === parent) {
          parent.insertBefore(element, nextSibling);
        } else {
          parent.appendChild(element);
        }
      });
      removedElements.desktop = [];
    }
  }

  removeElementsByScreenSize();
  window.addEventListener("resize", removeElementsByScreenSize);
}

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this._handleResize = this._debounce(
      this.initStickyAddToCart.bind(this),
      200
    );
  }

  connectedCallback() {
    this.initStickyAddToCart();

    window.addEventListener("resize", this._handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this._handleResize);
  }

  initStickyAddToCart() {
    const img = this.querySelector(".product-content img");
    const selectField = this.querySelector('select[name="id"]');
    const addButton = this.querySelector('button[name="add"]');
    const productForm = document.querySelector(".product-form");

    if (!selectField || !addButton || !productForm) return;

    const addToCartText = addButton.getAttribute("data-add-to-cart");
    const soldOutText = addButton.getAttribute("data-sold-out");

    const checkVisibility = () => {
    const formRect = productForm.getBoundingClientRect();
    const footer = document.querySelector("footer");
    const footerRect = footer?.getBoundingClientRect();
  
    const isFormVisible = formRect.top < window.innerHeight && formRect.bottom > 0;
    const isMobile = window.matchMedia("(max-width: 989px)").matches;
    const shouldShow = isMobile ? !isFormVisible : formRect.bottom <= 0;
    const isFooterVisible = footerRect && footerRect.top < window.innerHeight;
  
    if (shouldShow && !isFooterVisible) {
      this.classList.add("show");
      this.setAttribute("aria-hidden", "false");
      document.documentElement.style.paddingBottom = `${this.clientHeight}px`;
    } else {
      this.classList.remove("show");
      this.setAttribute("aria-hidden", "true");
      document.documentElement.style.paddingBottom = "0";
    }
  };

    checkVisibility();

    window.addEventListener("scroll", checkVisibility);

    selectField.addEventListener("change", () => {
      const selectedOption = selectField.options[selectField.selectedIndex];

      if (selectedOption.dataset.img) {
        img.setAttribute("src", selectedOption.dataset.img);
      }

      if (selectedOption.getAttribute("data-available") === "false") {
        addButton.setAttribute("disabled", "true");
        addButton.querySelector("span").textContent = soldOutText;
      } else {
        addButton.removeAttribute("disabled");
        addButton.querySelector("span").textContent = addToCartText;
      }
    });

    document
      .querySelector('.product-form [name="id"]')
      ?.addEventListener("change", (e) => {
        const value = Number(e.target.value);
        if (value) {
          selectField.value = value;
        }
      });
  }

  _debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

customElements.define("sticky-add-to-cart", StickyAddToCart);
